import React from 'react';
import { format } from 'date-fns';
import { User, Bot, Copy, Check } from 'lucide-react';
import { cn } from './utils';
import { useTranslation } from 'react-i18next';

const ChatMessages = ({ messages, isLoading, copiedIndex, copyToClipboard }) => {
    const { t } = useTranslation();
    const createIcon = (Icon, props = {}) => React.createElement(Icon, { ...props });

    return (
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 bg-gray-50/30">
            {messages.map((message, index) => (
                <div
                    key={index}
                    className={cn(
                        "flex gap-3 animate-in slide-in-from-bottom-2 duration-300 max-w-[80rem] mx-auto",
                        message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                    )}
                >
                    <div
                        className={cn(
                            "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center shadow-md",
                            message.role === 'user'
                                ? 'bg-gradient-to-br from-blue-600 to-indigo-700'
                                : 'bg-gradient-to-br from-gray-200 to-gray-300'
                        )}
                    >
                        {message.role === 'user'
                            ? createIcon(User, { className: "w-5 h-5 text-white" })
                            : createIcon(Bot, { className: "w-5 h-5 text-blue-700" })}
                    </div>

                    <div className={cn("flex-1", message.role === 'user' ? 'text-right' : 'text-left')}>
                        <div
                            className={cn(
                                "inline-block p-4 rounded-2xl shadow-sm max-w-xl",
                                message.role === 'user'
                                    ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white'
                                    : 'bg-white text-gray-800 border border-gray-200'
                            )}
                        >
                            <div
                                className="prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{
                                    __html: message.content.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>')
                                }}
                            />
                        </div>

                        <div className="flex items-center gap-2 mt-2 px-1">
                            <span className="text-xs text-gray-500">
                                {format(message.timestamp, 'h:mm a')}
                            </span>
                            {message.role === 'assistant' && (
                                <button
                                    onClick={() => copyToClipboard(message.content, index)}
                                    className="p-1 rounded hover:bg-gray-200 transition-colors"
                                    title={t('chatbot.messages.copy')}
                                >
                                    {copiedIndex === index
                                        ? createIcon(Check, { className: "w-3 h-3 text-green-600" })
                                        : createIcon(Copy, { className: "w-3 h-3 text-gray-500" })}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            ))}

            {isLoading && (
                <div className="flex gap-3 animate-in slide-in-from-bottom-2 duration-300 max-w-[80rem] mx-auto">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                        {createIcon(Bot, { className: "w-5 h-5 text-blue-700" })}
                    </div>
                    <div className="flex-1">
                        <div className="inline-block p-4 rounded-2xl bg-white border border-gray-200 shadow-sm">
                            <div className="flex gap-2">
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatMessages;